<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { saveEFilingData } from '@/features/startTask/api/taskApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import {
  TransferSharesForm,
  buildTransferTaskFormData,
  buildTransferEfilingSeedPayload,
  buildTransferPresetPrepareDocuments,
  buildDirectorsSignersJson,
  buildMembersSignersJson,
} from '@/features/startTask/preset_task/sg/components';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  editTaskId?: number | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
}>();

const router = useRouter();
const formRef = ref<InstanceType<typeof TransferSharesForm> | null>(null);
const creating = ref(false);
const formComplete = ref(false);
const progress = ref('');
const taskConfigSteps = ref<string[]>([]);

const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

const editMode = computed(() => !!props.editTaskId);
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true;
  if (editBaseline.value == null) return false;
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const icpCd = computed(() => props.entityData?.icpCd || props.entityData?.entityCd || '');
const companyName = computed(() =>
  String(props.entityData?.companyName || props.entityData?.entityName || props.entityData?.ENTITY_NAME || '').trim(),
);
const companyRegNo = computed(() =>
  String(props.entityData?.registrationNo || props.entityData?.regNo || props.entityData?.REG_NO || '').trim(),
);

function onFormValidityChange(valid: boolean) {
  formComplete.value = valid;
}

watch(() => props.entityData, (ent) => {
  if (!ent) formComplete.value = false;
});

onMounted(async () => {
  try {
    const taskCode = props.selection.task.TASK_CODE;
    if (taskCode) {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = cfg?.processSteps || [];
    }
  } catch (err) {
    console.warn('[PreSetTransferShares] task config fetch failed:', err);
  }
});

function gatherState(): Record<string, any> {
  const formData = formRef.value?.getFormData?.() || {};
  let cloned: Record<string, any> = {};
  try { cloned = JSON.parse(JSON.stringify(formData)); } catch { cloned = { ...formData }; }
  return { formData: cloned };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  const formData = s.formData && typeof s.formData === 'object' ? s.formData : s;
  nextTick(() => {
    if (formData && typeof formData === 'object') formRef.value?.setFormData?.(formData);
    nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
  });
}

defineExpose({ gatherState, restoreState });

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) {
    progress.value = 'Error: enter a Task Title in the chat first.';
    return;
  }
  if (!props.entityData) {
    progress.value = 'Error: select an entity in the chat first.';
    return;
  }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task code missing on preset.';
    return;
  }

  creating.value = true;
  progress.value = 'Validating transfer data...';
  try {
    const formValidation = await formRef.value?.validateAndFocusRequiredFields?.();
    if (formValidation === false) {
      progress.value = 'Error: complete all required transfer fields below.';
      creating.value = false;
      return;
    }

    const rawFormData = formRef.value?.getFormData?.();
    const eFilingData = await formRef.value?.getEFilingData?.();
    if (!rawFormData || typeof rawFormData !== 'object') {
      progress.value = 'Error: failed to collect transfer form data.';
      creating.value = false;
      return;
    }

    progress.value = editMode.value ? 'Updating task...' : 'Creating task...';
    const taskFormData = buildTransferTaskFormData(rawFormData as Record<string, any>, icpCd.value || '');
    (taskFormData as Record<string, any>).presetForm = gatherState();
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling'),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: taskFormData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* optional */ }
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: title,
          taskFlow,
          officerData: taskFormData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Saving e-filing seed...';
    const eSeed = buildTransferEfilingSeedPayload({
      eFilingDataValidation: eFilingData?.eFilingDataValidation,
      eFilingDataForApi: eFilingData?.eFilingDataForApi,
    });
    try {
      await saveEFilingData(taskId, {
        taskTypeCd,
        entityCd: icpCd.value || null,
        countryCd: 'SG',
        formData: taskFormData as Record<string, unknown>,
        requestPayload: eSeed as Record<string, unknown>,
        responseData: null,
        status: 'B36002',
        isSuccess: false,
      });
    } catch (e) {
      console.warn('[PreSetTransferShares] L_TASK_EFILING_DATA seed save failed', e);
    }

    try {
      await fetch(`/api/startTask/shares-transfer/update-efiling-data/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          eFilingDataValidation: eSeed.eFilingDataValidation,
          eFilingDataForApi: eSeed.eFilingDataForApi,
        }),
      });
    } catch (e) {
      console.warn('[PreSetTransferShares] TASK_DATA e-filing sync failed', e);
    }

    progress.value = 'Building documents...';
    const [directorsSignersJson, membersSignersJson] = await Promise.all([
      buildDirectorsSignersJson(icpCd.value || ''),
      buildMembersSignersJson(icpCd.value || ''),
    ]);
    const docsToSave = buildTransferPresetPrepareDocuments({
      selectionDocuments: (props.selection.documents || []).map((d: any) => ({
        tmpId: d.tmpId,
        templateName: d.templateName,
        docCode: d.docCode || d.DOC_TYPE || d.docType,
      })),
      formData: taskFormData,
      directorsSignersJson,
      membersSignersJson,
      personName: companyName.value || props.selection.task.TASK_NAME,
      entityCd: icpCd.value || undefined,
    });

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err: unknown) {
        console.warn('[PreSetTransferShares] prepare-documents failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch { /* ignore */ }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[PreSetTransferShares] create task failed:', err);
    progress.value = `Error: ${msg}`;
  } finally {
    creating.value = false;
  }
}
</script>

<template>
  <div class="ad-panel">
    <div class="ad-header">
      <div>
        <div class="ad-title">{{ selection.task.TASK_NAME }}</div>
        <div class="ad-subtitle">Transfer of Shares (Singapore PreSet)</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>
        Back
      </v-btn>
    </div>

    <div class="ad-content">
      <div class="ad-task-section">
        <div class="ad-task-section-header">
          <Icon icon="mdi:text-box-outline" width="16" color="#666" />
          <span>Task Title</span>
        </div>
        <div v-if="effectiveTaskTitle" class="d-flex align-start justify-space-between ga-3">
          <div class="ad-readonly-box flex-grow-1">{{ effectiveTaskTitle }}</div>
          <v-btn variant="tonal" color="primary" size="small" @click="emit('change-task-title')">
            <v-icon start size="16">mdi-pencil</v-icon>
            Change
          </v-btn>
        </div>
        <div v-else class="ad-readonly-box ad-readonly-empty">
          Waiting for task title (enter it in the chat on the left)...
        </div>
      </div>

      <div v-if="entityData" class="ad-entity">
        <div class="d-flex align-center ga-3 justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="ad-entity-icon">
              <Icon icon="mdi:domain" width="24" color="#1B84FF" />
            </div>
            <div>
              <div class="ad-entity-name">{{ companyName || '—' }}</div>
              <div v-if="companyRegNo" class="ad-entity-reg">UEN / Reg. No.: {{ companyRegNo }}</div>
            </div>
          </div>
          <v-btn variant="tonal" color="primary" size="small" @click="emit('change-entity')">
            <v-icon start size="16">mdi-swap-horizontal</v-icon>
            Change
          </v-btn>
        </div>
      </div>

      <div v-else class="ad-empty">
        <Icon icon="mdi:link-variant-plus" width="40" color="#d0d0d0" class="mb-2" />
        <div class="ad-empty-title">No entity linked</div>
        <div class="ad-empty-hint">Use the chat on the left to select a company.</div>
      </div>

      <div v-if="entityData" class="ad-form-wrap">
        <TransferSharesForm
          ref="formRef"
          :entity-id="icpCd"
          :read-only="false"
          :documents="selection.documents || []"
          :selected-person="selectedPerson || null"
          :active-person-context="activePersonContext || null"
          @request-person-lookup="(ctx) => emit('request-person-lookup', ctx)"
          @validity-change="onFormValidityChange"
        />
      </div>
    </div>

    <div v-if="creating || progress" class="ad-progress">
      <v-progress-linear v-if="creating" indeterminate color="primary" height="3" />
      <div class="ad-progress-text" :class="{ 'text-error': progress.startsWith('Error') }">
        {{ progress }}
      </div>
    </div>

    <div class="ad-footer">
      <v-btn variant="outlined" color="grey" size="small" @click="emit('back')">
        <Icon icon="mdi:arrow-left" width="16" class="mr-1" />
        Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :disabled="creating || !isReady || !formComplete || (editMode && !isDirty)"
              :loading="creating"
              @click="handleCreateTask"
            >
              <Icon :icon="editMode ? 'mdi:content-save' : 'mdi:check'" width="16" class="mr-1" />
              {{ editMode ? 'Update task' : 'Create task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<style scoped>
.ad-panel { height: 100%; display: flex; flex-direction: column; overflow: hidden; }
.ad-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 12px; border-bottom: 1px solid #f0f0f0; }
.ad-title { font-size: 18px; font-weight: 700; color: #1a1a2e; }
.ad-subtitle { font-size: 12px; color: #888; margin-top: 2px; }
.ad-content { flex: 1; overflow-y: auto; padding: 20px 24px; }
.ad-task-section { margin-bottom: 16px; background: #fff; border: 1px solid #e9edf5; border-radius: 12px; padding: 0 0 12px; overflow: hidden; }
.ad-task-section > :not(.ad-task-section-header) { padding-left: 14px; padding-right: 14px; }
.ad-task-section-header { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #283593; margin: 0 0 12px; padding: 10px 14px; background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%); border-bottom: 1px solid #d6dcf2; }
.ad-readonly-box { background: #fafafa; border: 1px solid #eee; border-radius: 6px; padding: 10px 12px; font-size: 13px; color: #333; line-height: 1.5; }
.ad-readonly-empty { color: #bbb; font-style: italic; }
.ad-entity { background: #f0f5ff; border: 1px solid #d6e4ff; border-radius: 12px; padding: 16px; margin-bottom: 16px; }
.ad-entity-icon { width: 40px; height: 40px; border-radius: 10px; background: #fff; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); }
.ad-entity-name { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.ad-entity-reg { font-size: 12px; color: #666; margin-top: 2px; }
.ad-empty { text-align: center; padding: 28px 16px; border: 1px dashed #ddd; border-radius: 12px; margin-bottom: 16px; }
.ad-empty-title { font-size: 14px; font-weight: 600; color: #666; }
.ad-empty-hint { font-size: 12px; color: #999; margin-top: 4px; }
.ad-progress { padding: 0 24px 12px; }
.ad-progress-text { font-size: 12px; color: #666; padding-top: 8px; }
.ad-footer { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 12px 24px 20px; border-top: 1px solid #f0f0f0; }
</style>
